/**
 * Operator-facing labels — collection final request prep recommendation UI (Slice D).
 */

import type { CollectionFinalRequestPrepDeferReasonCode } from '@shared/collection/collection-final-request-prep-disposition-types';
import type { CollectionFinalRequestPrepRecommendationCode } from '@shared/collection/collection-final-request-prep-recommendation-types';

export const COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS = {
  pageTitle: '최종 요청 준비 추천',
  pageSubtitle:
    'AR 건별 최종 요청·법무 인계 준비 추천을 확인하고 조치합니다. 추천은 자동 발송되지 않습니다.',
  panelTitle: '최종 요청 준비 추천 큐',
  sectionTitle: '최종 요청 준비 추천',
  boundaryCopy:
    '아래 추천은 최종 요청 자료 발송·법무 통지를 자동 실행하지 않습니다. 권한 있는 담당자의 별도 후속 조치가 필요합니다.',
  mandatoryCaveatTitle: '자동 발송 없음',
  mandatoryCaveatBody:
    '이 추천은 최종 요청 자료 발송·법무 통지를 자동 실행하지 않습니다. 승인 후에도 수동 후속 조치만 가능합니다.',
  legalHandoffCaveatBody:
    '법무 인계 준비 추천은 내부 검토용입니다. 법적 통지가 자동 발송되지 않습니다.',
  loadFailed: '최종 요청 준비 추천을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  permissionUnavailable: '최종 요청 준비 추천을 조회할 권한이 없습니다.',
  emptyQueue: '현재 검토 대기 중인 최종 요청 준비 추천이 없습니다.',
  emptyActive: '현재 활성 최종 요청 준비 추천이 없습니다.',
  loading: '최종 요청 준비 추천을 불러오는 중…',
  arEntryLabel: 'AR 건',
  generatedAtLabel: '추천 생성 시각',
  ruleProfileVersionLabel: '규칙 프로필 버전',
  rationaleLabel: '추천 사유',
  sourceSummaryLabel: '근거 요약',
  daysPastDueLabel: '연체일',
  outstandingAmountLabel: '미수 잔액',
  contactAttemptsLabel: '완료 연락 시도',
  stageLabel: '현재 단계',
  finalNoticeMaturityLabel: '최종 통지 성숙',
  dispositionLabel: '조치 상태',
  deferredBadge: '준비 보류',
  deferredUntil: '보류 만료',
  deferReason: '보류 사유',
  prepApprovedBadge: '준비 승인됨',
  prepApprovedAt: '승인 시각',
  dismissAction: '추천 기각',
  deferAction: '준비 보류',
  cancelDeferAction: '보류 해제',
  approveAction: '준비 승인',
  approveSuccessHint:
    '준비 승인 표시가 기록되었습니다. 최종 요청 자료 발송·법무 통지·AR 감소·단계 자동 진행은 발생하지 않습니다.',
  deferModalTitle: '최종 요청 준비 추천 보류',
  deferModalBody:
    '해당 추천만 지정 시점까지 목록에서 숨깁니다. 자동 발송·AR 변경·단계 자동 진행은 발생하지 않습니다.',
  deferReasonLabel: '보류 사유',
  deferReasonPlaceholder: '사유 선택',
  deferExpiryLabel: '보류 만료 시각',
  deferExpiryPlaceholder: '만료 시각 선택',
  deferNotesLabel: '메모 (선택)',
  deferNotesPlaceholder: '추가 메모',
  deferConfirm: '보류 적용',
  deferCancel: '취소',
  deferValidationReason: '보류 사유를 선택해 주세요.',
  deferValidationExpiry: '만료 시각을 선택해 주세요.',
  deferValidationExpiryFuture: '만료 시각은 현재 이후여야 합니다.',
  deferValidationNotesOther: '기타 사유는 메모가 필요합니다.',
  deferValidationNotesLength: '메모는 500자 이내로 입력해 주세요.',
  staleSourceMessage:
    '추천의 근거 정보가 변경되었습니다. 최신 상태를 불러온 뒤 다시 검토해 주세요.',
  inactiveRecommendationMessage: '해당 추천은 더 이상 조치할 수 없습니다. 목록을 새로고침합니다.',
  idempotencyConflictMessage: '동일 요청이 처리 중입니다. 잠시 후 다시 시도해 주세요.',
  mutationFailed: '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  permissionDismissDenied: '추천 기각 권한이 없습니다.',
  permissionDeferDenied: '준비 보류 권한이 없습니다.',
  permissionApproveDenied: '준비 승인 권한이 없습니다.',
  includeDeferredFilter: '보류 항목 포함',
  includeDeferredHint: '보류 중인 추천도 목록에 표시합니다.',
  refreshAfterMutation: '목록 새로고침',
  snapshotHashMissing: '추천 대상 식별 정보가 없어 조치할 수 없습니다.',
  notesMaxLength: 500,
  forbiddenCopyAutoSend: '자동 발송 예약',
  forbiddenCopyLegalSent: '법무 통지 발송',
  forbiddenCopyLetterGenerated: '최종 요청 서한 생성',
} as const;

const PREP_CODE_LABELS: Record<CollectionFinalRequestPrepRecommendationCode, string> = {
  final_payment_request_materials: '최종 납부 요청 자료 준비',
  legal_handoff_prep: '법무 인계 준비',
};

const DEFER_REASON_LABELS: Record<CollectionFinalRequestPrepDeferReasonCode, string> = {
  awaiting_customer_response: '고객 응답 대기',
  pending_internal_review: '내부 검토 대기',
  scheduled_follow_up: '예정 후속 조치',
  operator_capacity: '담당자 처리 용량',
  other: '기타',
};

export function labelFinalRequestPrepRecommendationCode(
  code: CollectionFinalRequestPrepRecommendationCode
): string {
  return PREP_CODE_LABELS[code] ?? code;
}

export function labelFinalRequestPrepDeferReason(
  code: CollectionFinalRequestPrepDeferReasonCode
): string {
  return DEFER_REASON_LABELS[code] ?? code;
}

export function formatFinalRequestPrepTimestamp(value: string): string {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return value;
  }
  return new Date(parsed).toLocaleString('ko-KR');
}

export function resolveFinalRequestPrepMandatoryCaveat(
  prepRecommendationCode: CollectionFinalRequestPrepRecommendationCode
): string {
  if (prepRecommendationCode === 'legal_handoff_prep') {
    return 'legal_handoff_prep_internal_only';
  }
  return 'final_request_prep_no_auto_send';
}
